import { ICommand } from 'core-ts-types';
import * as _4 from 'lodash4';
import { PlatformUtility } from 'utility-ts-platform';
import { TypeUtility } from 'utility-ts-type';
import { PathItem } from '../../data/builders/path_item_builder';
import { PathTopic } from '../../data/builders/path_topic_builder';
import { PathTitleColorService } from '../../data/services/path_color_service';
import { PathModalService } from './../../modals/path_modal_service';

export class PathTaskCardController {
  private task: any;
  private topic: PathTopic;
  private item: PathItem;
  private format: any;
  private taskTitleClass: string;
  private isMobile: boolean;

  constructor(
    private $state: ng.ui.IStateService,
    private ngPathTaskFormatter: any,
    private taskUpdateFn: any,
    private TaskStatusEnum: any,
    private editReminderCommand: ICommand,
    private completeStaffTodoCommand: ICommand,
    private $scope: ng.IScope,
    private ngTypeUtility: typeof TypeUtility,
    private pathTitleColorService: PathTitleColorService,
    private ngPathModalService: PathModalService
  ) {}

  isPersonalReminder(): boolean {
    return this.taskUpdateFn.isPersonalReminder(this.task);
  }
  isCompleted(): boolean {
    return this.taskUpdateFn.isCompleted(this.task);
  }
  isDismissed(): boolean {
    return this.taskUpdateFn.isDismissed(this.task);
  }
  isPendingComplete(): boolean {
    return this.taskUpdateFn.isPendingComplete(this.task);
  }
  isDateMarkerTask(): boolean {
    return this.taskUpdateFn.isDateMarker(this.task);
  }
  isActive(): boolean {
    return this.task.show;
  }
  isAutoCompletable(): boolean {
    return this.taskUpdateFn.isAutoCompletable(this.task);
  }
  isOverdue(): boolean {
    return !this.isAppointment() && !this.isAppointmentRequest() && this.taskUpdateFn.isOverdue(this.task);
  }
  isAppointment(): boolean {
    return this.ngPathTaskFormatter.isAppointment(this.task, this.item);
  }
  isAppointmentRequest(): boolean {
    return this.ngPathTaskFormatter.isAppointmentRequest(this.task, this.item);
  }
  isArticle(): boolean {
    return this.ngPathTaskFormatter.isArticle(this.task, this.item);
  }
  isPlacementTest(): boolean {
    return this.ngPathTaskFormatter.isPlacementTest(this.task, this.item);
  }
  isStaffDrivenToDo(): boolean {
    return this.taskUpdateFn.isStaffDrivenToDo(this.task);
  }

  showCalendar(): boolean {
    return this.isAppointment() || this.isDateMarkerTask();
  }

  getSuperTitle(): string {
    if (this.isPersonalReminder()) {
      return 'PERSONAL TO-DO';
    }
    return !_4.isEmpty(this.topic) ? this.topic.name : '';
  }

  setTaskTitleClass(): void {
    this.taskTitleClass = this.pathTitleColorService.getTextColorClass(this.getSuperTitle());
  }

  getTitle(): string {
    if (this.isPersonalReminder()) {
      return this.task.title;
    }
    if (this.isStaffDrivenToDo()) {
      return this.task.name;
    }
    if (this.task.substitute_text_by_field_by_item[this.item.id]) {
      return this.task.substitute_text_by_field_by_item[this.item.id].title;
    }
    return this.item.title;
  }

  getStaffLabel(): string {
    return `Sent by ${this.task.created_by_name}`;
  }

  getDateLabel(): string {
    return this.format.getPathItemDateText(this.task, this.isOverdue(), false);
  }

  getAXDateLabel(): string {
    return this.format.getPathItemDateText(this.task, this.isOverdue(), true);
  }

  get taskTitleId() {
    return `taskTitle${this.task.id}`;
  }

  get stateParams() {
    if (this.isPersonalReminder()) {
      return {
        personalTodoID: this.task.id
      };
    }

    if (this.isStaffDrivenToDo()) {
      return {
        staffTodoID: this.task.id
      };
    }

    return {
      eventId: this.task.event,
      repeatedBy: this.task.repeated_by,
      topic: _4.get(this.topic, 'name')
    };
  }

  get stateUri() {
    if (this.isPersonalReminder()) {
      return `my.todos.reminder`;
    }
    if (this.isStaffDrivenToDo()) {
      return `my.todos.stafftodo`;
    }

    if (this.isAppointment()) {
      return `my.todos.appointment`;
    } else if (this.isAppointmentRequest()) {
      return `my.todos.appointmentRequest`;
    } else if (this.isPlacementTest()) {
      return `my.todos.placement`;
    } else {
      return `my.todos.details`;
    }
  }

  goToStateOnMobileOnly() {
    if (this.isMobile) {
      this.$state.go(this.stateUri, this.stateParams);
    }
  }

  updateStatus(): void {
    if (this.isDismissed()) {
      this.toggleDismissed();
    } else if (this.isPersonalReminder()) {
      this.updateReminderStatus();
    } else if (this.isStaffDrivenToDo()) {
      this.updateStaffTodoStatus();
    } else {
      this.updatePathTask();
    }
  }

  updatePathTask(): void {
    const initialStatus = this.task.status;
    if (this.isAutoCompletable()) {
      this.task.is_pending_complete = !this.task.is_pending_complete;
    }
    this.taskUpdateFn.toggleComplete(this.task, initialStatus, this.task.is_pending_complete);
  }

  updateReminderStatus(): void {
    const status = _4.isEqual(this.task.status, 'ACTIVE') ? 'COMPLETED' : 'ACTIVE';
    this.editReminderCommand(this.task.id, { status });
  }

  updateStaffTodoStatus(): void {
    this.completeStaffTodoCommand(this.task.id, this.isCompleted()).then((response: { reminder: any }) => {
      this.task.cleared_date = this.isCompleted() ? '' : moment().format('YYYY-MM-DD');
    });
  }

  toggleDismissed(): void {
    const initialStatus = this.task.status;
    this.task.status = this.taskUpdateFn.getNewStatusAsString(this.task, initialStatus, true);
    this.taskUpdateFn.toggleDismissed(this.task, initialStatus, false);
  }

  showWhatIsAutoCompleteModal($event: any): void {
    $event.stopPropagation();
    this.ngPathModalService.toggleAutoCompleteModal(true);
  }

  getIconDate(): string {
    return this.format.getCalendarDay(this.task);
  }

  $onInit(): void {
    this.format = this.ngPathTaskFormatter;
    this.setTaskTitleClass();
    this.isMobile = PlatformUtility.isMobile();
  }

  $onChanges(changes: ng.IChangesObject): void {
    this.setTaskTitleClass();
  }
}

export class PathTaskCardComponent implements angular.IComponentOptions {
  public templateUrl: string = 'capability-ng-todos/src/components/path_task_card/path_task_card.html';
  public controller: any = PathTaskCardController;
  public controllerAs: string = '$ctrl';
  public bindings: any = {
    task: '<',
    item: '<',
    topic: '<'
  };
}



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-todos/src/components/path_task_card/path_task_card.ts